import { Link2, ShieldCheck } from "lucide-react";
import { useCallback, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { Identity } from "@/modules/dashboard/cards/types/identities-api.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { VerificationService } from "../services/verificationService";
import type { KycStatus } from "../types/verification-api.types";
import type { VerificationResult } from "../types/verification.types";
import { DocumentsReadOnlyList } from "./DocumentsReadOnlyList";
import { KycStatusPill } from "./KycStatusPill";
import { VerificationDialog } from "./VerificationDialog";

const RESULT_TO_KYC: Record<VerificationResult, KycStatus> = {
  verified: "VERIFIED",
  review: "IN_REVIEW",
  rejected: "REJECTED",
  expired: "EXPIRED",
};

/**
 * Marco C — verification panel for an identity's detail page. Shows the current KYC/KYB status, the actions
 * to launch the embedded engine or issue a resumable link to send (marco B), and the read-only §6B
 * documents.
 *
 * PARKED, pending a product decision. Its only mount point wraps it in `<InternalOnly>`, and both of its
 * actions are blocked for INTERNAL users — so in practice the panel is a status header plus a documents list,
 * and neither button is reachable. The buttons stay rendered and disabled rather than deleted because the
 * question is which user type should own these actions, not whether they should exist.
 *
 * The `isInternalUser` conditions therefore read as always-true today. They are kept because they state the
 * rule (issuing a verification link belongs to the holder's organization, not to Coloca staff) and because
 * the gate above it may move.
 */
export function IdentityVerificationSection({ identity }: { identity: Identity }) {
  const { t } = useTranslations("verification");
  const { isInternalUser } = useAuth();
  const purpose = identity.type === "business" ? "KYB" : "KYC";

  const [status, setStatus] = useState<KycStatus>(identity.kyc_status);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);

  const isVerified = status === "VERIFIED";

  const handleIssueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const { data } = await VerificationService.issueLink(identity.id);
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.verification_url);
      }
      colocaToast.success(t("link_copied"));
    } catch {
      colocaToast.error(t("error_generic_description"));
    } finally {
      setLinkLoading(false);
    }
  }, [identity.id, t]);

  const handleResult = useCallback((result: VerificationResult) => {
    setStatus(RESULT_TO_KYC[result]);
  }, []);

  return (
    <section className="space-y-5 rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{t("verify_dialog_title")}</h3>
          <KycStatusPill status={status} />
        </div>
        {!isVerified ? (
          <div className="flex flex-wrap items-center gap-2">
            <ColocaButton
              variant="default"
              size="sm"
              icon={Link2}
              loading={linkLoading}
              onClick={handleIssueLink}
              // Same endpoint as "Regenerate link" in the KYC section above, and
              // issuing invalidates whatever link is already out there — gating
              // one issuer and not the other would only move the click.
              disabled={isInternalUser}
            >
              {t("link_generate")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              size="sm"
              icon={ShieldCheck}
              onClick={() => setDialogOpen(true)}
              disabled={isInternalUser}
            >
              {t("verify_action")}
            </ColocaButton>
          </div>
        ) : null}
      </div>

      <div>
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("documents_title")}
        </h4>
        <DocumentsReadOnlyList identityId={identity.id} hasDocument={isVerified} />
      </div>

      <VerificationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        identityId={identity.id}
        purpose={purpose}
        onResult={handleResult}
      />
    </section>
  );
}
